<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO, isValid } from 'date-fns';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: { type: Number, required: true },
  quoteNumber: { type: String, default: '' },
  title: { type: String, default: '' },
  totalAmount: { type: Number, default: 0 },
  currency: { type: String, default: 'USD' },
  status: { type: String, default: 'draft' },
  validUntil: { type: String, default: '' },
  companyName: { type: String, default: '' },
  contactName: { type: String, default: '' },
  userName: { type: String, default: '' },
  updatedAt: { type: [String, Number], default: null },
  isExpanded: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'showQuote']);

const { t } = useI18n();

const onClickViewDetails = () => emit('showQuote', props.id);

const displayTitle = computed(() => props.title || t('QUOTES.UNTITLED'));
const displayQuoteNumber = computed(() => props.quoteNumber || '-');

const statusColor = computed(() => {
  const colors = {
    draft: 'bg-n-slate-9/10 text-n-slate-11',
    sent: 'bg-n-blue-9/10 text-n-blue-11',
    accepted: 'bg-n-teal-9/10 text-n-teal-11',
    rejected: 'bg-n-ruby-9/10 text-n-ruby-11',
    expired: 'bg-n-amber-9/10 text-n-amber-11',
  };
  return colors[props.status] || colors.draft;
});

const formattedAmount = computed(() => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: props.currency || 'USD',
    minimumFractionDigits: 0,
  }).format(props.totalAmount);
});

const formattedUpdatedAt = computed(() => {
  if (!props.updatedAt) return '';
  try {
    const date = typeof props.updatedAt === 'string' ? parseISO(props.updatedAt) : new Date(props.updatedAt);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
});

const isExpired = computed(() => {
  if (!props.validUntil) return false;
  try {
    const date = parseISO(props.validUntil);
    return isValid(date) && date < new Date();
  } catch {
    return false;
  }
});
</script>

<template>
  <CardLayout
    layout="row"
    class="cursor-pointer transition-all duration-200 hover:outline-n-brand hover:shadow-md group/cardLayout"
    :class="{
      'outline-n-brand': isExpanded,
    }"
    @click="!isExpanded && onClickViewDetails()"
  >
    <div class="flex items-center justify-start flex-1 gap-4">
      <Avatar
        :username="displayTitle"
        class="shrink-0"
        :name="title"
        :size="48"
        hide-offline-status
        rounded-full
      />
      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
          <span class="text-sm text-n-slate-11 font-mono">
            #{{ displayQuoteNumber }}
          </span>
          <span class="text-base font-medium truncate text-n-slate-12">
            {{ displayTitle }}
          </span>
          <span
            :class="['px-2 py-0.5 rounded-full text-xs font-medium', statusColor]"
          >
            {{ $t(`QUOTES.STATUS.${status.toUpperCase()}`) }}
          </span>
          <span v-if="isExpired && status !== 'accepted' && status !== 'rejected'" class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-amber-9/10 text-n-amber-11">
            {{ $t('QUOTES.EXPIRED') }}
          </span>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <span class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <span class="font-medium text-n-slate-12">{{ formattedAmount }}</span>
            </span>
            <div v-if="companyName" class="w-px h-3 bg-n-slate-6" />
            <span v-if="companyName" class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-building-2" size="size-3.5 text-n-slate-11" />
              {{ companyName }}
            </span>
            <div v-if="contactName" class="w-px h-3 bg-n-slate-6" />
            <span v-if="contactName" class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-user" size="size-3.5 text-n-slate-11" />
              {{ contactName }}
            </span>
            <div v-if="validUntil" class="w-px h-3 bg-n-slate-6" />
            <span v-if="validUntil" class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-calendar" size="size-3.5 text-n-slate-11" />
              {{ validUntil }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <span
              v-if="updatedAt"
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 flex-shrink-0"
            >
              {{ formattedUpdatedAt }}
            </span>
            <Button
              :label="$t('QUOTES.VIEW_DETAILS')"
              variant="link"
              size="xs"
              class="opacity-0 group-hover/cardLayout:opacity-100 transition-opacity"
              @click.stop="onClickViewDetails"
            />
          </div>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
